import React, { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import api from '../lib/axios';

/**
 * SecureFileLink
 *
 * Downloads a protected Cloudinary asset entirely through our backend.
 * The browser never sees any Cloudinary URL, API key, or signature —
 * only the raw bytes piped from our streaming proxy endpoint.
 *
 * @param {string}   endpoint  Backend download endpoint (e.g., '/opportunities/123/applications/456/resume/download')
 * @param {string}   label     Filename shown to the user and used as the download filename
 * @param {string}   [className] Optional custom CSS class
 */
const SecureFileLink = ({ endpoint, label, className }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch binary through our authenticated axios instance.
      // The Authorization: Bearer <JWT> header is attached automatically.
      // responseType: 'blob' tells axios to materialise the stream as a Blob.
      const response = await api.get(endpoint, { responseType: 'blob' });

      // Map MIME type → correct file extension so the saved file always opens correctly.
      // response.data is a Blob — its .type is set from the server Content-Type header
      // and is always available even without CORS Access-Control-Expose-Headers.
      const MIME_TO_EXT = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      };

      const mimeType = response.data.type; // e.g. "image/png", "application/pdf"
      const detectedExt = MIME_TO_EXT[mimeType]; // undefined for octet-stream
      // Strip any extension already in label and rebuild with detected one
      const baseName = label.replace(/\.[^.]+$/, '') || label;

      let downloadName;
      if (detectedExt) {
        // Primary: use actual MIME type — most reliable, always works
        downloadName = `${baseName}.${detectedExt}`;
      } else {
        // Fallback 1: Content-Disposition header (only works if CORS exposes it)
        const disposition = response.headers['content-disposition'];
        if (disposition) {
          const match = disposition.match(/filename="?([^";\r\n]+)"?/i);
          if (match) downloadName = match[1];
        }
        // Fallback 2: keep original label unchanged
        if (!downloadName) downloadName = label;
      }
      const anchor = document.createElement('a');
      anchor.download = downloadName;

      // Create a temporary object URL from the Blob and trigger the download
      const blobUrl = URL.createObjectURL(response.data);
      anchor.href = blobUrl;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Release the object URL so the browser can reclaim memory
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('[SecureFileLink] Download failed:', err);
      setError(err.response?.data?.message || 'Download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const defaultClasses =
    'inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50';

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title={error || label}
      className={className || defaultClasses}
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin text-gray-400" />
      ) : (
        <FileText size={14} className="text-gray-400" />
      )}
      {label}
    </button>
  );
};

export default SecureFileLink;
